import { EntityKind } from "./types";

/**
 * The app's vocabulary for each RMS record kind. Single definition — the panel,
 * the dashboard, setup, and backups all read it, so the names cannot drift
 * (#11238). `RMS/utils` re-exports this rather than keeping a second copy.
 *
 * `label` is the singular the user sees; `singular` is the lowercase code
 * name. Both forms are spelled out rather than derived, because
 * pluralization (`Companies`, `People`) follows no rule.
 */
export interface EntityKindInfo {
  key: EntityKind;
  label: string;
  singular: string;
  displayLabel: string;
}

// The record-type phrase a filter clause uses ("Is a Company", "is an
// Investment Vehicle"): the map's `label` with its indefinite article (#11674).
// One helper so the panel rows and the chip strip cannot disagree on either
// the name or the article. Falls back to the raw kind for an unknown value
// (a saved view from a newer schema) rather than rendering nothing.
export const entityKindClausePhrase = (kind: string): string => {
  const label = entityKindMap[kind as EntityKind]?.label ?? kind;
  const article = /^[aeiou]/i.test(label) ? "an" : "a";
  return `${article} ${label}`;
};

export const entityKindMap: Record<EntityKind, EntityKindInfo> = {
  manager: {
    key: "manager",
    label: "Manager",
    singular: "manager",
    displayLabel: "Managers",
  },
  // "Vehicle", not "Investment Vehicle" (#11426): the mock's vocabulary, and
  // the qualifier spent width everywhere the word appears without telling an
  // investment platform's user anything.
  vehicle: {
    key: "vehicle",
    label: "Vehicle",
    singular: "vehicle",
    displayLabel: "Vehicles",
  },
  company: {
    key: "company",
    label: "Company",
    singular: "company",
    displayLabel: "Companies",
  },
  person: {
    key: "person",
    label: "Person",
    singular: "person",
    displayLabel: "People",
  },
  portfolio: {
    key: "portfolio",
    label: "Portfolio",
    singular: "portfolio",
    displayLabel: "Portfolios",
  },
  client: {
    key: "client",
    label: "Client",
    singular: "client",
    displayLabel: "Clients",
  },
  deal: {
    key: "deal",
    label: "Deal",
    singular: "deal",
    displayLabel: "Deals",
  },
  topic: {
    key: "topic",
    label: "Topic",
    singular: "topic",
    displayLabel: "Topics",
  },
  interaction: {
    key: "interaction",
    label: "Interaction",
    singular: "interaction",
    displayLabel: "Interactions",
  },
};

/** Narrows a kind read off the wire or a URL to one the app models. */
export const isEntityKind = (kind: unknown): kind is EntityKind =>
  typeof kind === "string" && Object.hasOwn(entityKindMap, kind);

// Entity types for Setup page (excluding interaction). Also the picker for
// changing a record's type (#11700): the backend validates against the same
// eight in `ChangeableKind` (fp/rms/change_kind.py) — add a ninth to both.
export const setupEntityTypes: EntityKind[] = [
  "manager",
  "vehicle",
  "person",
  "company",
  "topic",
  "client",
  "portfolio",
  "deal",
];

// Whether a settings subtab segment names a record kind — the class of
// pages that render as the rail-less two-pane editor (gaps-v11 1a pattern,
// #11426). The settings shell and the record-model page both read THIS
// predicate, so the rail and the page shape can never desynchronise.
export const isSetupEntityKind = (key: string | undefined): boolean =>
  key !== undefined && (setupEntityTypes as readonly string[]).includes(key);
